import { FormEvent, useState } from 'react';
import { LangSwitch } from '../components/LangSwitch';
import { Link, useNavigate } from 'react-router-dom';
import { IconArrowRight, IconBolt, IconShield, IconWaves } from '../components/icons';
import { Logo, OceanBackdrop } from '../components/ocean';
import { startAuthentication } from '@simplewebauthn/browser';
import { MfaStep } from '../components/MfaStep';
import { Field } from '../components/ui';
import { api, ApiError, needsOnboarding } from '../lib/api';
import { useSession } from '../lib/session';
import { readFirstTouch } from '../lib/activity';
import { t } from '../lib/i18n';

export function AuthPage({ mode }: { mode: 'login' | 'signup' }) {
  const { refresh } = useSession();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', name: '', company: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  async function passkey() {
    setError('');
    try {
      const { options, state } = await api.post<{ options: Parameters<typeof startAuthentication>[0]['optionsJSON']; state: string }>('/auth/passkey/options');
      const response = await startAuthentication({ optionsJSON: options });
      await api.post('/auth/passkey', { response, state });
      await finish();
    } catch (err) {
      const name = (err as Error).name;
      if (name === 'NotAllowedError' || name === 'AbortError') return; // user closed the browser prompt
      setError(err instanceof ApiError ? t('Этот passkey не подошёл. Войдите паролем.') : t('Устройство не поддерживает passkeys или вход отменён.'));
    }
  }

  async function finish() {
    const me = await refresh();
    nav(me && needsOnboarding(me) ? '/onboarding' : '/', { replace: true });
  }
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setErrors({});
    try {
      // First-touch attribution (UTM, external referrer, landing page) is sent once, with the sign-up.
      if (mode === 'signup') await api.post('/auth/signup', { ...form, ...(readFirstTouch() ? { attribution: readFirstTouch() } : {}) });
      else {
        const res = await api.post<{ mfa_required?: boolean; mfa_token?: string }>('/auth/login', { email: form.email, password: form.password });
        if (res.mfa_required && res.mfa_token) return setMfaToken(res.mfa_token);
      }
      await finish();
    } catch (err) {
      if (err instanceof ApiError && err.details) {
        setErrors(Object.fromEntries(err.details.map((d) => [d.path, d.message])));
      }
      setError(
        err instanceof ApiError
          ? err.code === 'INVALID_CREDENTIALS'
            ? t('Неверный email или пароль')
            : err.code === 'EMAIL_TAKEN'
              ? t('Аккаунт с этим email уже существует')
              : err.code === 'VALIDATION_FAILED'
                ? t('Проверьте поля формы')
                : err.code === 'RATE_LIMITED'
                  ? t('Слишком много попыток. Подождите минуту.')
                  : err.message
          : t('Сеть недоступна. Попробуйте ещё раз.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ocean-screen">
      <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}><LangSwitch /></div>
      <OceanBackdrop />
      <div className="auth-wrap">
        <section className="auth-hero">
          <div className="row" style={{ marginBottom: 28 }}>
            <Logo size={44} />
            <strong style={{ fontSize: 22, color: '#fff' }}>MailOcean</strong>
          </div>
          <h1>
            {t('Каждое письмо — ')}<em>{t('волна,')}</em>
            <br />
            {t('которая доходит до берега.')}
          </h1>
          <p>{t('Транзакционные письма для продуктов, которым нельзя терять ни одного чека, кода или подтверждения.')}</p>
          <ul>
            <li><IconBolt /> {t('Идемпотентный API: повтор запроса не создаёт дубль')}</li>
            <li><IconWaves /> {t('Путь каждого письма с точностью до миллисекунды')}</li>
            <li><IconShield /> {t('Раздельные потоки: отписка от рассылок не блокирует сброс пароля')}</li>
          </ul>
        </section>

        {mfaToken ? (
          <div className="glass auth-card stack">
            <h2>{t('Ещё один шаг')}</h2>
            <MfaStep token={mfaToken} onDone={finish} />
          </div>
        ) : (
        <form className="glass auth-card stack" onSubmit={submit} noValidate>
          <div>
            <h2>{mode === 'signup' ? t('Начать погружение') : t('С возвращением на борт')}</h2>
            <p className="muted" style={{ marginTop: 6 }}>
              {mode === 'signup' ? t('Аккаунт и рабочее пространство за минуту.') : t('Войдите, чтобы продолжить.')}
            </p>
          </div>
          {mode === 'signup' && (
            <>
              <Field label={t('Ваше имя')} error={errors.name}>
                <input className="input" value={form.name} onChange={set('name')} autoComplete="name" required />
              </Field>
              <Field label={t('Компания')} error={errors.company}>
                <input className="input" value={form.company} onChange={set('company')} autoComplete="organization" required />
              </Field>
            </>
          )}
          <Field label={t('Рабочий email')} error={errors.email}>
            <input className="input" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
          </Field>
          <Field label={t('Пароль')} error={errors.password} hint={mode === 'signup' ? t('Минимум 10 символов') : undefined}>
            <input
              className="input"
              type="password"
              value={form.password}
              onChange={set('password')}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
            />
          </Field>
          {mode === 'login' && (
            <Link to="/forgot-password" className="small" style={{ alignSelf: 'flex-end', marginTop: -6 }}>{t('Забыли пароль?')}</Link>
          )}
          {error && <div className="callout bad" role="alert">{error}</div>}
          <button className="btn btn-primary btn-lg" disabled={busy}>
            {busy ? t('Погружаемся…') : mode === 'signup' ? t('Создать аккаунт') : t('Войти')} <IconArrowRight size={16} />
          </button>
          {mode === 'login' && (
            <button type="button" className="btn btn-lg" style={{ background: 'rgba(2,12,29,.45)', color: '#e4f1fb', borderColor: 'rgba(139,227,236,.25)' }} onClick={passkey}>
              🔑 {t('Войти с passkey')}
            </button>
          )}
          <p className="muted small" style={{ textAlign: 'center' }}>
            {mode === 'signup' ? (
              <>{t('Уже есть аккаунт?')} <Link to="/login">{t('Войти')}</Link></>
            ) : (
              <>{t('Впервые здесь?')} <Link to="/signup">{t('Создать аккаунт')}</Link></>
            )}
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
